import React, { useEffect, useRef } from 'react';
import { announceToScreenReader } from '../../utils/accessibility';
import './BottomTabs.css';

/**
 * Tab types for mobile navigation
 */
export type MobileTab = 'grid' | 'tracks' | 'sound' | 'song';

/**
 * Props for BottomTabs component
 * 
 * Requirements: 6.1, 6.2, 6.7, 6.8, 16.1, 16.2, 16.3, 16.5
 */
export interface BottomTabsProps {
  /** Currently active tab */
  activeTab: MobileTab;
  /** Callback when tab is changed */
  onTabChange: (tab: MobileTab) => void;
}

/**
 * BottomTabs Component
 * 
 * Primary navigation for mobile UI, providing access to different views.
 * Displays four tabs: Grid, Tracks, Sound, Song.
 * Each tab is 25% width with 44x44px minimum touch target size.
 * Active tab is highlighted with accent color.
 * Uses sticky positioning at bottom of screen.
 * 
 * Requirements: 6.1, 6.2, 6.7, 6.8, 16.1, 16.2, 16.3, 16.5
 */
export const BottomTabs: React.FC<BottomTabsProps> = ({
  activeTab,
  onTabChange,
}) => {
  const previousTabRef = useRef<MobileTab>(activeTab);

  /**
   * Tab configuration with icons and labels
   */
  const tabs: Array<{ id: MobileTab; icon: string; label: string; ariaLabel: string }> = [
    {
      id: 'grid',
      icon: 'fa-grid-2',
      label: 'Grid',
      ariaLabel: 'Grid view - Edit step sequencer',
    },
    {
      id: 'tracks',
      icon: 'fa-list',
      label: 'Tracks',
      ariaLabel: 'Tracks panel - View and manage all tracks',
    },
    {
      id: 'sound',
      icon: 'fa-waveform-lines',
      label: 'Sound',
      ariaLabel: 'Sound panel - Edit instrument parameters and effects',
    },
    {
      id: 'song',
      icon: 'fa-music',
      label: 'Song',
      ariaLabel: 'Song settings - Adjust BPM, steps, and composition settings',
    },
  ];

  /**
   * Announce tab changes to screen readers
   * Requirement 16.5: Screen reader announcements for state changes
   */
  useEffect(() => {
    if (previousTabRef.current !== activeTab) {
      const tab = tabs.find(t => t.id === activeTab);
      if (tab) {
        announceToScreenReader(`Switched to ${tab.label} tab`);
      }
      previousTabRef.current = activeTab;
    }
  }, [activeTab, tabs]);

  /**
   * Handle keyboard navigation
   * Requirement 16.3: Keyboard navigation support
   */
  const handleKeyDown = (e: React.KeyboardEvent, tabId: MobileTab) => {
    const currentIndex = tabs.findIndex(t => t.id === tabId);
    
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
      const prevTab = tabs[prevIndex];
      if (prevTab) {
        onTabChange(prevTab.id);
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
      const nextTab = tabs[nextIndex];
      if (nextTab) {
        onTabChange(nextTab.id);
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      const firstTab = tabs[0];
      if (firstTab) {
        onTabChange(firstTab.id);
      }
    } else if (e.key === 'End') {
      e.preventDefault();
      const lastTab = tabs[tabs.length - 1];
      if (lastTab) {
        onTabChange(lastTab.id);
      }
    }
  };

  return (
    <nav className="bottom-tabs" role="tablist" aria-label="Mobile navigation">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`bottom-tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => onTabChange(tab.id)}
          onKeyDown={(e) => handleKeyDown(e, tab.id)}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-label={tab.ariaLabel}
          aria-controls={`${tab.id}-panel`}
          title={tab.label}
          tabIndex={activeTab === tab.id ? 0 : -1}
        >
          <i className={`fa-solid ${tab.icon}`} aria-hidden="true"></i>
          <span className="bottom-tab-label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
};
